import { useEffect, useMemo, useRef, useState } from 'react';
import type { PropsWithChildren, ReactNode } from 'react';
import { ThemeProvider, useMediaQuery, useTheme } from '@mui/material';
import { createHelixTheme } from '@design-system/theme/createHelixTheme';
import { AuthVisualContext, type AuthVisualState } from './authVisual';
import { stopAuthKeySound } from './authKeySound';
import { CheckCircledIcon, ExclamationTriangleIcon } from '@radix-ui/react-icons';
import { useTranslation } from 'react-i18next';
import './authExperience.css';

export function AuthBrand({ hero = false }: { hero?: boolean }) {
  return (
    <div className={`auth-brand${hero ? ' auth-brand-hero' : ''}`}>
      <span className="auth-brand-mark" aria-hidden="true">
        H
      </span>
      <span>
        <span className="auth-brand-name">Helix</span>
        <span className="auth-brand-tagline">Support Cloud</span>
      </span>
    </div>
  );
}

// Decorative rendering receives masked characters only, never the password or OTP.
function AuthTypewriter({ value }: { value: AuthVisualState }) {
  const frame = useRef<HTMLIFrameElement>(null);
  const latest = useRef(value);
  const desktop = useMediaQuery('(min-width: 1025px)');
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const send = () =>
    frame.current?.contentWindow?.postMessage(
      {
        type: 'helix-login',
        email: latest.current.email,
        password: latest.current.passwordMask,
        reducedMotion,
      },
      window.location.origin,
    );
  useEffect(() => {
    latest.current = value;
    frame.current?.contentWindow?.postMessage(
      { type: 'helix-login', email: value.email, password: value.passwordMask, reducedMotion },
      window.location.origin,
    );
  }, [value, reducedMotion, desktop]);
  return (
    <aside className="auth-hero" aria-label="Helix Support Cloud">
      <AuthBrand hero />
      {desktop && (
        <iframe
          ref={frame}
          src="/auth-typewriter.html"
          title="Helix 3D typewriter"
          onLoad={send}
          tabIndex={-1}
          sandbox="allow-scripts allow-same-origin"
          allow="autoplay"
        />
      )}
    </aside>
  );
}

export function AuthFrame({ children }: PropsWithChildren) {
  const { t } = useTranslation();
  const outerTheme = useTheme();
  const theme = useMemo(
    () => createHelixTheme('light', outerTheme.direction),
    [outerTheme.direction],
  );
  const [value, setValue] = useState<AuthVisualState>({ email: '', passwordMask: '' });
  useEffect(() => () => stopAuthKeySound(), []);
  return (
    <ThemeProvider theme={theme}>
      <AuthVisualContext.Provider value={setValue}>
        <main className="helix-auth">
          <AuthTypewriter value={value} />
          <section className="auth-form-panel">
            <div className="auth-form-wrap">
              <AuthBrand />
              <div className="auth-card">{children}</div>
              <p className="auth-footer">{t('auth.protected')}</p>
            </div>
          </section>
        </main>
      </AuthVisualContext.Provider>
    </ThemeProvider>
  );
}

export function AuthResult({
  success,
  title,
  message,
  children,
  icon,
}: PropsWithChildren<{
  success: boolean;
  title: string;
  message: string;
  icon?: ReactNode;
}>) {
  return (
    <div className="auth-result" role={success ? 'status' : 'alert'} aria-live="polite">
      <span
        className={`auth-status-icon${success ? ' auth-status-success' : ' auth-status-error'}`}
        aria-hidden="true"
      >
        {icon ??
          (success ? (
            <CheckCircledIcon width={26} height={26} />
          ) : (
            <ExclamationTriangleIcon width={24} height={24} />
          ))}
      </span>
      <h1 className="auth-heading">{title}</h1>
      <p className="auth-description">{message}</p>
      {children}
    </div>
  );
}

export function AuthHeading({
  title,
  description,
  icon,
}: {
  title: string;
  description: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <header className={`auth-intro${icon ? ' auth-intro-otp' : ''}`}>
      {icon && (
        <span className="auth-status-icon" aria-hidden="true">
          {icon}
        </span>
      )}
      <h1 className="auth-heading">{title}</h1>
      <div className="auth-description">{description}</div>
    </header>
  );
}
